// Constants
import { AlertUserDebounceInterval } from "@ms/uno-constants/lib/local/NotificationConstants";
// Controls
import { Button, Tooltip } from "@fluentui/react-components";
import { BackgroundPicker } from "./backgroundPicker/BackgroundPicker";
import { ContainerType } from "@ms/planner-models/lib/local/service/planner/enumerations/IPlannerEnumerations";
// Resources
import { Strings } from "@ms/uno-resources/lib/local/CultureUtilities";
// Styles
import { usePlanSettingsStyles } from "./PlanSettings.styles";
// Utilities
import debounce from "lodash/debounce";
import * as React from "react";
/**
 * PlanSettings component for displaying and managing plan settings
 *
 * This presentational component provides a settings interface that includes:
 * - Background image selection with AI-powered suggestions
 * - Plan deletion functionality (when permitted)
 * - Loading and error states for image suggestions
 *
 * @param props - Component properties
 * @returns JSX element representing plan settings interface
 */ export const PlanSettings = ({ plan, canDeletePlan, isGroupOwner, canUpdateVisuals, isTabView, currentBackgroundUrl, backgroundSuggestions, fetchImageSuggestions, onUpdatePlanTitle, onDeletePlan, onUpdatePlanBackground, areBackgroundSuggestionsEnabled = true })=>{
    const styles = usePlanSettingsStyles();
    /**
     * Debounced function to fetch image suggestions to prevent excessive API calls
     * Uses standard alert user debounce interval for consistency with other components
     */ const debouncedFetchImageSuggestions = React.useMemo(()=>{
        return debounce((id, value)=>{
            fetchImageSuggestions?.(id, value);
        }, AlertUserDebounceInterval);
    }, [
        fetchImageSuggestions
    ]);
    // Fetch image suggestions when plan id or display name changes
    React.useEffect(()=>{
        debouncedFetchImageSuggestions.cancel();
        if (areBackgroundSuggestionsEnabled && fetchImageSuggestions) {
            fetchImageSuggestions(plan.id, plan.displayName);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        plan.id,
        plan.displayName,
        areBackgroundSuggestionsEnabled
    ]);
    // Cleanup function for debounced fetch on unmount
    React.useEffect(()=>{
        return ()=>{
            debouncedFetchImageSuggestions.cancel();
        };
    }, [
        debouncedFetchImageSuggestions
    ]);
    /**
     * Handle background selection by user
     * Notifies parent component to update the background URL
     * Converts empty string to null for clearing background (service expects null, not empty string)
     * @param imageUrl - The selected background image URL (empty string for no background)
     */ const handleBackgroundSelect = (imageUrl)=>{
        // Convert empty string to null - the service expects null for no background
        const normalizedUrl = imageUrl || null;
        if (onUpdatePlanBackground) {
            onUpdatePlanBackground(plan.id, normalizedUrl);
        }
    };
    /**
     * Handle delete plan button click
     * Delegates to parent component callback with current plan ID
     */ const handleDeletePlan = ()=>{
        onDeletePlan(plan.id);
    };
    // Group non-owner who can't delete: keep the button focusable so the owner-only Tooltip can explain why.
    // Owners who still can't delete (archived, creating, or sensitivity-restricted) get a plain disabled button.
    const cannotDeleteGroupPlanNotOwner = !canDeletePlan && plan.container.containerType === ContainerType.Group && isGroupOwner === false;
    const deleteButton = /*#__PURE__*/ React.createElement(Button, {
        "data-testid": "plan-settings-button",
        appearance: "outline",
        onClick: handleDeletePlan,
        className: styles.deleteButton,
        disabled: !canDeletePlan && !cannotDeleteGroupPlanNotOwner,
        disabledFocusable: cannotDeleteGroupPlanNotOwner
    }, Strings.PlanSettings_DeletePlan);
    return /*#__PURE__*/ React.createElement("div", {
        "data-testid": "plan-settings",
        className: styles.root
    }, /*#__PURE__*/ React.createElement("div", {
        className: styles.content
    }, areBackgroundSuggestionsEnabled && /*#__PURE__*/ React.createElement("div", {
        className: styles.backgroundSection
    }, /*#__PURE__*/ React.createElement(BackgroundPicker, {
        suggestions: backgroundSuggestions,
        selectedUrl: currentBackgroundUrl,
        onSelect: handleBackgroundSelect,
        canUpdateBackground: canUpdateVisuals
    })), !isTabView && /*#__PURE__*/ React.createElement("div", {
        className: styles.footer
    }, cannotDeleteGroupPlanNotOwner ? /*#__PURE__*/ React.createElement(Tooltip, {
        content: Strings.PlanSettings_CannotDeletePlan,
        relationship: "description",
        positioning: "above",
        withArrow: true
    }, deleteButton) : deleteButton)));
};
